import type { Persona } from '@/stores'

export type PersonaScope = 'all' | 'mine' | 'public' | 'shared'
export type PersonaTypeFilter = 'chat' | 'voice' | 'avatar' | 'managed'

export interface PersonaFilterState {
  scope: PersonaScope
  tags: string[]
  types: PersonaTypeFilter[]
}

export const SCOPES: PersonaScope[] = ['all', 'mine', 'public', 'shared']
export const TYPE_FILTERS: PersonaTypeFilter[] = ['chat', 'voice', 'avatar', 'managed']
export const EMPTY_FILTERS: PersonaFilterState = { scope: 'all', tags: [], types: [] }

export const MAX_TAGS = 20
export const MAX_TAG_LEN = 32

export function normalizeTag(raw: string): string {
  return raw.trim().toLowerCase().slice(0, MAX_TAG_LEN)
}

export function normalizeTags(list: string[]): string[] {
  const out: string[] = []
  for (const raw of list) {
    const t = normalizeTag(raw)
    if (t && !out.includes(t)) out.push(t)
  }
  return out.slice(0, MAX_TAGS)
}

export function applyPersonaFilters(
  personas: Persona[],
  state: PersonaFilterState,
  currentUserId?: string,
): Persona[] {
  return personas.filter((p) => {
    if (state.scope === 'mine' && p.userId !== currentUserId) return false
    if (state.scope === 'public' && !p.isPublic) return false
    if (state.scope === 'shared' && !p.isShared) return false

    if (state.tags.length > 0) {
      const pt = p.tags ?? []
      if (!state.tags.some((t) => pt.includes(t))) return false
    }

    if (state.types.length > 0) {
      const matches = state.types.some((t) =>
        t === 'managed' ? (p.source ?? 'manual') !== 'manual' : p.presentationMode === t,
      )
      if (!matches) return false
    }

    return true
  })
}

export function computeTagUniverse(personas: Persona[]): { tag: string; count: number }[] {
  const counts = new Map<string, number>()
  for (const p of personas) {
    for (const t of p.tags ?? []) {
      const key = normalizeTag(t)
      if (!key) continue
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
  }
  return [...counts.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag))
}

export function countActiveFilters(state: PersonaFilterState): number {
  return (state.scope !== 'all' ? 1 : 0) + state.tags.length + state.types.length
}

export function parseFiltersFromParams(params: URLSearchParams): PersonaFilterState {
  const scope = params.get('scope')
  const tags = params.get('tags')
  const types = params.get('type')
  return {
    scope: SCOPES.includes(scope as PersonaScope) ? (scope as PersonaScope) : 'all',
    tags: tags ? normalizeTags(tags.split(',')) : [],
    types: types
      ? types
          .split(',')
          .map((s) => s.trim())
          .filter((t): t is PersonaTypeFilter => TYPE_FILTERS.includes(t as PersonaTypeFilter))
      : [],
  }
}

export function writeFiltersToParams(
  prev: URLSearchParams,
  state: PersonaFilterState,
): URLSearchParams {
  const next = new URLSearchParams(prev)
  if (state.scope !== 'all') next.set('scope', state.scope)
  else next.delete('scope')
  if (state.tags.length) next.set('tags', state.tags.join(','))
  else next.delete('tags')
  if (state.types.length) next.set('type', state.types.join(','))
  else next.delete('type')
  return next
}
